import React, { useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../contexts/AuthContext';

export const StorageBucketFix: React.FC = () => {
  const { isAdmin } = useAuth();
  const [fixing, setFixing] = useState(false);
  const [results, setResults] = useState<string[]>([]);

  const log = (message: string) => {
    setResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const fixStorageBucket = async () => {
    setFixing(true);
    setResults([]);
    
    try {
      log('Starting storage bucket fix...');
      
      // Step 1: List existing buckets
      const { data: existingBuckets, error: listError } = await supabase.storage.listBuckets();
      log(`Current buckets: ${existingBuckets?.map(b => b.id).join(', ') || 'none'}`);
      
      // Step 2: Try to delete the bucket if it exists
      const pluginBucket = existingBuckets?.find(b => b.id === 'plugin-files');
      if (pluginBucket) {
        log('Removing existing bucket...');
        const { error: deleteError } = await supabase.storage.deleteBucket('plugin-files');
        if (deleteError) {
          log(`Warning: Could not delete existing bucket: ${deleteError.message}`);
        } else {
          log('✅ Existing bucket removed');
        }
      }
      
      // Step 3: Create the bucket through the API
      log('Creating bucket through Supabase API...');
      const { data: createData, error: createError } = await supabase.storage.createBucket('plugin-files', {
        public: false,
        fileSizeLimit: 104857600, // 100MB
        allowedMimeTypes: [
          'application/java-archive',
          'application/zip', 
          'application/x-java-archive',
          'application/octet-stream'
        ]
      });
      
      if (createError) {
        log(`❌ Bucket creation failed: ${createError.message}`);
        
        // Try alternative approach - just ensure it exists
        log('Trying alternative approach...');
        const { data: bucketsAfter, error: listAfterError } = await supabase.storage.listBuckets();
        if (!listAfterError) {
          const bucketExists = bucketsAfter?.find(b => b.id === 'plugin-files');
          if (bucketExists) {
            log('✅ Bucket exists and is accessible');
          } else {
            log('❌ Bucket still not accessible');
          }
        }
      } else {
        log('✅ Bucket created successfully through API');
      }
      
      // Step 4: Test bucket access
      log('Testing bucket access...');
      const { data: finalBuckets, error: finalListError } = await supabase.storage.listBuckets();
      
      if (finalListError) {
        log(`❌ Error listing buckets: ${finalListError.message}`);
      } else {
        const bucket = finalBuckets?.find(b => b.id === 'plugin-files');
        if (bucket) {
          log('✅ Plugin-files bucket is now accessible!');
          log(`Bucket details: ${bucket.name}, Public: ${bucket.public}`);
        } else {
          log('❌ Plugin-files bucket still not found');
        }
      }
      
      // Step 5: Test file operations
      if (finalBuckets?.find(b => b.id === 'plugin-files')) {
        log('Testing file upload capability...');
        try {
          const testFile = new File(['test content'], 'test.txt', { type: 'text/plain' });
          const { error: uploadError } = await supabase.storage
            .from('plugin-files')
            .upload('test-file.txt', testFile);
          
          if (uploadError) {
            log(`❌ Upload test failed: ${uploadError.message}`);
          } else {
            log('✅ Upload test successful');
            
            // Clean up test file
            await supabase.storage.from('plugin-files').remove(['test-file.txt']);
            log('✅ Test file cleaned up');
          }
        } catch (err) {
          log(`❌ Upload test error: ${err}`);
        }
      }
      
      log('Storage bucket fix completed!');
      
    } catch (error) {
      log(`❌ Fix error: ${error}`);
    } finally {
      setFixing(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-lg font-semibold text-red-800 mb-2">Access Denied</h3>
        <p className="text-red-600">This storage fix tool is only available to administrators.</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-xl font-bold mb-4">Storage Bucket Fix</h2>
      <p className="text-gray-600 mb-4">
        This tool will properly create the plugin-files storage bucket through the Supabase API.
      </p>
      
      <button
        onClick={fixStorageBucket}
        disabled={fixing}
        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 mb-4"
      >
        {fixing ? 'Fixing Storage Bucket...' : 'Fix Storage Bucket'}
      </button>
      
      {results.length > 0 && (
        <div className="mt-4">
          <h3 className="text-lg font-semibold mb-2">Results:</h3>
          <div className="bg-gray-100 p-4 rounded max-h-96 overflow-y-auto">
            {results.map((result, index) => (
              <div
                key={index}
                className={`text-sm font-mono mb-1 ${
                  result.includes('❌') ? 'text-red-600' :
                  result.includes('✅') ? 'text-green-600' :
                  'text-gray-700'
                }`}
              >
                {result}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}; 